import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useEffect, useRef } from 'react';

import uniqueId from 'lodash/uniqueId';
import { useSearchParams } from 'react-router';

import type { UIMessage } from '@tanstack/ai-client';

interface Options {
  conversationId: string | null;
  messagesRef: RefObject<UIMessage[]>;
  setMessages: (messages: UIMessage[]) => void;
  setAnimatedHumanId: Dispatch<SetStateAction<string | null>>;
  setAnimatedText: Dispatch<SetStateAction<string | null>>;
}

/**
 * Drives the `?prompt=` deep-link flow (e.g. a shared-prompt link): a prompt
 * carried in the URL should play the same typing-animation experience as a
 * user typing and sending a message, then actually get sent once the
 * animation finishes.
 *
 * Design note (Task 8 — resolving a gap left open by Task 7):
 * The old chat client let `sendMessage(text, { addHuman: false })` send a
 * message WITHOUT appending a new human entry, because this hook pre-emptively
 * pushed an optimistic "animated" human message into `messages` itself, and
 * later relied on `addHuman: false` to avoid a duplicate. `useChat()` from
 * `@tanstack/ai-react` has no such option: every `sendMessage()` call
 * unconditionally appends its own user message, and `ChatProvider.tsx`'s
 * `sendMessage` (Task 7, not modified by this task) already reflects that —
 * it accepts an `options.addHuman` parameter for API compatibility but never
 * reads it.
 *
 * If this hook still wrote the optimistic message into `messages` here AND
 * `ChatProvider.onAnimatedComplete` calls `sendMessage(animatedText)` once
 * typing finishes, the prompt would be added twice: once by this hook, once
 * by `useChat()`'s own auto-add. To avoid that duplicate, this hook no longer
 * writes to `messages` at all. It only sets `animatedHumanId`/`animatedText`,
 * which is sufficient to drive the typing-animation UI — the actual message
 * is added exactly once, by `sendMessage(animatedText)` inside
 * `onAnimatedComplete` when the animation completes.
 *
 * `messagesRef`/`setMessages` are kept in `Options` only so this hook's call
 * site in `ChatProvider.tsx` doesn't need to change; they are intentionally
 * unused here. A later task (adapting `HumanMessage`/`useHumanMessage`) needs
 * to render the animated bubble directly off `animatedText`/`animatedHumanId`
 * rather than by matching `animatedHumanId` against an entry in `messages`,
 * since no such entry exists until the animation completes.
 */
export function useUrlPromptIntegration({
  conversationId,
  setAnimatedHumanId,
  setAnimatedText
}: Options) {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    if (hasProcessedRef.current) return;
    const prompt = searchParams.get('prompt');
    if (!prompt || !conversationId) return;

    hasProcessedRef.current = true;

    setAnimatedHumanId(uniqueId('prompt-'));
    setAnimatedText(prompt);

    setSearchParams(
      prev => {
        prev.delete('prompt');
        return prev;
      },
      { replace: true }
    );
  }, [conversationId, searchParams, setSearchParams, setAnimatedHumanId, setAnimatedText]);
}
